import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, ArrowLeft, User, Phone, Mail, Shield, DollarSign, Clock, FileText, Globe, Lock, X, CheckCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import SEOHead from '../components/SEOHead';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FRAUD_TYPES = [
  { id: 'investment', icon: '📊' },
  { id: 'pyramid', icon: '🔺' },
  { id: 'internet', icon: '🌐' },
  { id: 'crypto', icon: '₿' },
  { id: 'employment', icon: '💼' },
];

const AMOUNTS = ['1 000', '5 000', '10 000', '20 000', '50 000'];
const TOTAL_STEPS = 3;

// Country code detection by browser language
const getPhonePrefix = () => {
  const lang = (navigator.language || navigator.userLanguage || 'ru').toLowerCase();
  if (lang.startsWith('ar')) return '+971 ';
  if (lang.startsWith('uk') || lang.startsWith('ua')) return '+380 ';
  if (lang.startsWith('kk') || lang.startsWith('kz')) return '+7 ';
  if (lang.startsWith('uz')) return '+998 ';
  if (lang.startsWith('tr')) return '+90 ';
  if (lang.startsWith('de')) return '+49 ';
  if (lang.startsWith('fr')) return '+33 ';
  if (lang.startsWith('en-gb') || lang.startsWith('en-GB')) return '+44 ';
  if (lang.startsWith('en')) return '+1 ';
  if (lang.startsWith('he') || lang.startsWith('iw')) return '+972 ';
  if (lang.startsWith('pl')) return '+48 ';
  return '+7 ';
};

export default function LeadFormPage() {
  const { t, currentLang, changeLang, isRTL } = useLanguage();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [showExit, setShowExit] = useState(false);
  const [exitShown, setExitShown] = useState(false);

  const [form, setForm] = useState({
    name: '', phone: getPhonePrefix(), email: '',
    fraud_type: '', amount: '', time_elapsed: '', description: '',
  });

  // Urgency: random slots 2-4, stays same per session
  const [slots] = useState(() => {
    const saved = sessionStorage.getItem('capitara_slots');
    if (saved) return parseInt(saved);
    const s = Math.floor(Math.random() * 3) + 2;
    sessionStorage.setItem('capitara_slots', s.toString());
    return s;
  });

  // Exit-intent detection
  const handleMouseLeave = useCallback((e) => {
    if (e.clientY <= 0 && !exitShown) {
      setShowExit(true);
      setExitShown(true);
    }
  }, [exitShown]);

  // Mobile: detect back button / visibility change
  const handleVisChange = useCallback(() => {
    if (document.visibilityState === 'hidden' && !exitShown) {
      setShowExit(true);
      setExitShown(true);
    }
  }, [exitShown]);

  useEffect(() => {
    document.addEventListener('mouseleave', handleMouseLeave);
    document.addEventListener('visibilitychange', handleVisChange);
    return () => {
      document.removeEventListener('mouseleave', handleMouseLeave);
      document.removeEventListener('visibilitychange', handleVisChange);
    };
  }, [handleMouseLeave, handleVisChange]);

  const fraudLabels = {
    investment: t.formFraudInvestment, pyramid: t.formFraudPyramid,
    internet: t.formFraudInternet, crypto: t.formFraudCrypto,
    employment: t.formFraudEmployment || 'Трудоустройство обман',
  };

  const timeOptions = [
    { id: 'month', label: t.formTimeMonth },
    { id: '2months', label: t.formTime2Months },
    { id: '3months', label: t.formTime3Months },
    { id: 'more', label: t.formTimeMore },
  ];

  const phoneDigits = form.phone.replace(/[^\d]/g, '');
  const isValidPhone = phoneDigits.length >= 10;
  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email.trim());

  const canNext = () => {
    if (step === 1) return form.name.trim().length >= 2 && isValidPhone && isValidEmail;
    if (step === 2) return !!form.fraud_type && !!form.amount;
    if (step === 3) return !!form.time_elapsed;
    return false;
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setErrorMsg('');
    try {
      const params = new URLSearchParams(window.location.search);
      const source = params.get('source') || '';
      const res = await axios.post(`${API_URL}/api/contact`, {
        name: form.name, phone: form.phone, email: form.email,
        amount: form.amount ? `$${form.amount}` : '',
        fraud_type: form.fraud_type,
        time_elapsed: form.time_elapsed,
        description: form.description || '-',
        source: source,
      });
      if (res.data.success) {
        sessionStorage.setItem('capitara_lead_submitted', 'true');
        sessionStorage.setItem('capitara_lead_value', form.amount ? form.amount.replace(/\s/g, '') : '0');
        navigate('/successful');
      } else { setErrorMsg(res.data.message); }
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (Array.isArray(detail)) {
        const msgs = detail.map(d => d.msg || '').filter(Boolean);
        setErrorMsg(msgs.join('. ') || t.formErrorGeneral || 'Проверьте введённые данные');
      } else if (typeof detail === 'string') {
        setErrorMsg(detail);
      } else {
        setErrorMsg(t.formErrorGeneral || 'Произошла ошибка. Попробуйте ещё раз.');
      }
    }
    setSubmitting(false);
  };

  const next = () => { if (step < TOTAL_STEPS && canNext()) setStep(s => s + 1); };
  const back = () => { if (step > 1) setStep(s => s - 1); };
  const handleKeyDown = (e) => { if (e.key === 'Enter' && canNext()) { e.preventDefault(); if (step < TOTAL_STEPS) next(); } };
  const languages = ['RU', 'EN', 'AR'];

  return (
    <div className="lf-page" dir={isRTL ? 'rtl' : 'ltr'} data-testid="lead-form-page">
      <SEOHead page="form" />

      {/* Compact header */}
      <div className="lf-header">
        <div className="lf-logo" onClick={() => navigate('/')}>CAPITARA</div>
        <div className="lf-lang">
          {languages.map(l => (
            <button key={l} onClick={() => changeLang(l.toLowerCase())}
              className={`lf-lang-btn ${currentLang === l.toLowerCase() ? 'active' : ''}`}>{l}</button>
          ))}
        </div>
      </div>

      {/* Social proof + Urgency */}
      <div className="lf-trust-bar" data-testid="trust-bar">
        <span className="lf-trust-proof"><CheckCircle size={13} /> {t.formSocialProof}</span>
        <span className="lf-trust-divider">|</span>
        <span className="lf-trust-avg">{t.formAvgReturn}</span>
      </div>
      <div className="lf-urgency" data-testid="urgency-bar">
        <span className="lf-urgency-dot" />
        {t.formUrgency} <strong>{slots}</strong> {t.formUrgencySlots}
      </div>

      {/* Progress */}
      <div className="lf-progress">
        <div className="lf-progress-bar">
          <div className="lf-progress-fill" style={{ width: `${(step / TOTAL_STEPS) * 100}%` }} />
        </div>
        <span className="lf-step-label">{step}/{TOTAL_STEPS}</span>
      </div>

      {/* Card */}
      <div className="lf-card">

        {/* Step 1: Contacts */}
        {step === 1 && (
          <div data-testid="step-1" className="lf-step">
            <h2 className="lf-title"><User size={18} color="var(--brand-primary)" /> {t.formStepContacts}</h2>

            <label className="lf-label">{t.formStepName}</label>
            <input data-testid="input-name" type="text" value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })} onKeyDown={handleKeyDown}
              placeholder={t.formStepNamePlaceholder} className="lf-input" autoFocus />

            <label className="lf-label" style={{ marginTop: 12 }}>{t.phone}</label>
            <input data-testid="input-phone" type="tel" inputMode="tel" value={form.phone}
              onChange={e => {
                const val = e.target.value.replace(/[^\d+\s\-()]/g, '');
                setForm({ ...form, phone: val });
              }} onKeyDown={handleKeyDown}
              placeholder="+7 (999) 123-45-67" className="lf-input" />
            {form.phone && phoneDigits.length > 3 && !isValidPhone && (
              <span className="lf-hint" data-testid="phone-hint">{t.formPhoneHint}</span>
            )}

            <label className="lf-label" style={{ marginTop: 12 }}>{t.email}</label>
            <input data-testid="input-email" type="email" inputMode="email" value={form.email}
              onChange={e => setForm({ ...form, email: e.target.value })} onKeyDown={handleKeyDown}
              placeholder="your@email.com" className="lf-input" />
            {form.email && !isValidEmail && (
              <span className="lf-hint" data-testid="email-hint">{t.formEmailHint}</span>
            )}
          </div>
        )}

        {/* Step 2: Fraud type + Amount */}
        {step === 2 && (
          <div data-testid="step-2" className="lf-step">
            <h2 className="lf-title"><Shield size={18} color="var(--brand-primary)" /> {t.formStepFraud}</h2>
            <div className="lf-fraud-grid">
              {FRAUD_TYPES.map(ft => (
                <button key={ft.id} data-testid={`fraud-${ft.id}`}
                  onClick={() => setForm({ ...form, fraud_type: ft.id })}
                  className={`lf-fraud-btn ${form.fraud_type === ft.id ? 'selected' : ''}`}>
                  <span className="lf-fraud-icon">{ft.icon}</span>
                  <span className="lf-fraud-label">{fraudLabels[ft.id]}</span>
                </button>
              ))}
            </div>

            <label className="lf-label" style={{ marginTop: 16 }}><DollarSign size={14} /> {t.formStepAmount}</label>
            <div className="lf-chips">
              {AMOUNTS.map(amt => (
                <button key={amt} data-testid={`amount-${amt.replace(/\s/g, '')}`}
                  onClick={() => setForm({ ...form, amount: form.amount === amt ? '' : amt })}
                  className={`lf-chip ${form.amount === amt ? 'selected' : ''}`}>
                  ${amt}
                </button>
              ))}
              <button data-testid="amount-more"
                onClick={() => setForm({ ...form, amount: form.amount === '50 000+' ? '' : '50 000+' })}
                className={`lf-chip ${form.amount === '50 000+' ? 'selected' : ''}`}>
                $50k+
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Time + Description + Submit */}
        {step === 3 && (
          <div data-testid="step-3" className="lf-step">
            <label className="lf-label"><Clock size={14} /> {t.formStepTime}</label>
            <div className="lf-chips">
              {timeOptions.map(opt => (
                <button key={opt.id} data-testid={`time-${opt.id}`}
                  onClick={() => setForm({ ...form, time_elapsed: form.time_elapsed === opt.id ? '' : opt.id })}
                  className={`lf-chip ${form.time_elapsed === opt.id ? 'selected' : ''}`}>
                  {opt.label}
                </button>
              ))}
            </div>

            <label className="lf-label" style={{ marginTop: 16 }}><FileText size={14} /> {t.formStepDescription} <span className="lf-opt">({t.formOptional})</span></label>
            <textarea data-testid="input-description" value={form.description}
              onChange={e => setForm({ ...form, description: e.target.value })}
              placeholder={t.formStepDescriptionPlaceholder}
              className="lf-textarea" rows={3} />
          </div>
        )}

        {errorMsg && <div className="lf-error" data-testid="form-error">{errorMsg}</div>}

        {/* Security guarantee */}
        <div className="lf-secure" data-testid="security-badge">
          <Lock size={12} /> {t.formSecure}
        </div>

        {/* Navigation */}
        <div className="lf-nav">
          {step > 1 ? (
            <button onClick={back} className="lf-back" data-testid="btn-back">
              <ArrowLeft size={16} /> {t.formBack}
            </button>
          ) : <div />}
          {step < TOTAL_STEPS ? (
            <button onClick={next} disabled={!canNext()} className={`lf-next ${!canNext() ? 'disabled' : ''}`} data-testid="btn-next">
              {t.formNext} <ArrowRight size={16} />
            </button>
          ) : (
            <button onClick={handleSubmit} disabled={!canNext() || submitting}
              className={`lf-submit ${!canNext() || submitting ? 'disabled' : ''}`} data-testid="btn-submit">
              {submitting ? t.formSubmitting : t.formSubmit} {!submitting && <ArrowRight size={16} />}
            </button>
          )}
        </div>
      </div>

      {/* Exit-intent popup */}
      {showExit && (
        <div className="lf-exit-overlay" data-testid="exit-popup" onClick={() => setShowExit(false)}>
          <div className="lf-exit-modal" onClick={e => e.stopPropagation()}>
            <button className="lf-exit-close" onClick={() => setShowExit(false)} data-testid="exit-close"><X size={18} /></button>
            <div className="lf-exit-icon"><Shield size={32} color="var(--brand-primary)" /></div>
            <h3 className="lf-exit-title">{t.formExitTitle}</h3>
            <p className="lf-exit-text">{t.formExitText}</p>
            <button className="lf-exit-btn" onClick={() => setShowExit(false)} data-testid="exit-return-btn">
              {t.formExitBtn} <ArrowRight size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
